package com.binayshaw7777.myportfolio.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent

sealed class CursorMode {
    data object Normal : CursorMode()
    data class CTA(val text: String = "Learn more") : CursorMode()
}

val LocalCursorMode = staticCompositionLocalOf { mutableStateOf<CursorMode>(CursorMode.Normal) }

@Composable
fun Modifier.changeCursorMode(text: String = "Learn more"): Modifier {
    val cursorModeState = LocalCursorMode.current

    return this
        .onMouseEnter { cursorModeState.value = CursorMode.CTA(text) }
        .onMouseLeave { cursorModeState.value = CursorMode.Normal }
}


@Composable
fun Cursor() {
    val cursorMode = LocalCursorMode.current.value

    val isCTA = cursorMode is CursorMode.CTA
    val ctaText = (cursorMode as? CursorMode.CTA)?.text ?: "Learn more"

    DotCursor(visible = !isCTA)
    CircleCursor(visible = !isCTA)

    CtaCursor(
        text = ctaText,
        visible = isCTA
    )
}

@OptIn(ExperimentalComposeWebApi::class)
@Composable
fun CtaCursor(
    text: String = "Learn more",
    speed: Double = 0.35, // smoother: 0.15, snappier: 0.3,
    visible: Boolean
) {
    var cursorX by remember { mutableStateOf<Double?>(null) }
    var cursorY by remember { mutableStateOf<Double?>(null) }

    var mouseX by remember { mutableStateOf<Double?>(null) }
    var mouseY by remember { mutableStateOf<Double?>(null) }

    // Track mouse movement
    DisposableEffect(Unit) {
        val mouseMoveListener = { e: Event ->
            val m = e as MouseEvent
            val x = m.clientX.toDouble()
            val y = m.clientY.toDouble()

            mouseX = x
            mouseY = y

            // ✅ initialize once so it doesn't spawn from (0,0)
            if (cursorX == null || cursorY == null) {
                cursorX = x
                cursorY = y
            }
        }

        window.addEventListener("mousemove", mouseMoveListener)
        onDispose { window.removeEventListener("mousemove", mouseMoveListener) }
    }

    // Smooth follow
    LaunchedEffect(Unit) {
        while (true) {
            val mx = mouseX
            val my = mouseY
            val cx = cursorX
            val cy = cursorY

            if (mx != null && my != null && cx != null && cy != null) {
                cursorX = cx + (mx - cx) * speed
                cursorY = cy + (my - cy) * speed
            }
            delay(16)
        }
    }

    val cx = cursorX ?: return
    val cy = cursorY ?: return

    val cursorColor = if (ColorMode.current.isLight) Colors.Black else Colors.White
    val textColor = if (ColorMode.current.isLight) Colors.White else Colors.Black

    // CTA cursor
    Div(
        attrs = Modifier
            .padding(leftRight = 14.px, topBottom = 10.px)
            .styleModifier {
                position(Position.Fixed)
                left(0.px)
                top(0.px)
                borderRadius(999.px)

                backgroundColor(cursorColor)
                color(textColor)

                fontSize(14.px)
                fontWeight(600)
                whiteSpace(WhiteSpace.NoWrap)
                letterSpacing(0.3.px)

                transform {
                    translateX(cx.px + 18.px)
                    translateY(cy.px - 14.px)
                }

                opacity(if (visible) 1.0 else 0.0)
                property(
                    "transform",
                    "translate(${cx + 18}px, ${cy - 14}px) scale(${if (visible) 1.0 else 0.92})"
                )
                property("transition", "opacity 200ms ease")

                pointerEvents(PointerEvents.None)
                zIndex(2147483647)
            }
            .toAttrs()
    ) {
        SpanText(
            text = "$text ↗",
            modifier = Modifier
                .fontSize(14.px)
                .fontWeight(FontWeight.SemiBold)
        )
    }
}


@OptIn(ExperimentalComposeWebApi::class)
@Composable
fun CircleCursor(visible: Boolean) {
    // State to track the circle's position
    var circleX by remember { mutableStateOf<Double?>(null) }
    var circleY by remember { mutableStateOf<Double?>(null) }

    var mouseX by remember { mutableStateOf<Double?>(null) }
    var mouseY by remember { mutableStateOf<Double?>(null) }

    // Speed factor (0 = smoother, 1 = instant)
    val speed = 0.15
    val scale = if (visible) 1.0 else 0.85


    // Track mouse movement
    DisposableEffect(Unit) {
        val mouseMoveListener = { e: Event ->
            val m = e as MouseEvent
            val x = m.clientX.toDouble()
            val y = m.clientY.toDouble()

            mouseX = x
            mouseY = y

            // ✅ init once
            if (circleX == null || circleY == null) {
                circleX = x
                circleY = y
            }
        }

        window.addEventListener("mousemove", mouseMoveListener)
        onDispose { window.removeEventListener("mousemove", mouseMoveListener) }
    }

    LaunchedEffect(Unit) {
        while (true) {
            val mx = mouseX
            val my = mouseY
            val cx = circleX
            val cy = circleY

            if (mx != null && my != null && cx != null && cy != null) {
                circleX = cx + (mx - cx) * speed
                circleY = cy + (my - cy) * speed
            }
            delay(16)
        }
    }

    val cx = circleX ?: return
    val cy = circleY ?: return

    Div(
        attrs = Modifier
            .classNames("circle")
            .styleModifier {
                position(Position.Fixed)
                left(0.px)
                top(0.px)

                width(20.px)
                height(20.px)
                border(1.px, LineStyle.Solid, Colors.Black)
                borderRadius(50.percent)

                property("filter", "invert(1)")
                property("mix-blend-mode", "difference")

                opacity(if (visible) 1.0 else 0.0)
                property("transition", "opacity 220ms ease")

                transform {
                    translateX(cx.px - 10.px)
                    translateY(cy.px - 10.px)
                    scale(scale)
                }


                pointerEvents(PointerEvents.None)
                zIndex(Int.MAX_VALUE)
            }
            .toAttrs()
    )
}

@OptIn(ExperimentalComposeWebApi::class)
@Composable
fun DotCursor(visible: Boolean) {
    var mouseX by remember { mutableStateOf(0.0) }
    var mouseY by remember { mutableStateOf(0.0) }

    DisposableEffect(Unit) {
        val listener = { e: Event ->
            val m = e as MouseEvent
            mouseX = m.clientX.toDouble()
            mouseY = m.clientY.toDouble()
        }
        window.addEventListener("mousemove", listener)
        onDispose { window.removeEventListener("mousemove", listener) }
    }

    Div(
        attrs = Modifier.styleModifier {
            position(Position.Fixed)
            left(0.px)
            top(0.px)

            width(6.px)
            height(6.px)
            borderRadius(50.percent)
            backgroundColor(Colors.Black)

            property("filter", "invert(1)")
            property("mix-blend-mode", "difference")

            transform {
                translateX(mouseX.px - 3.px)
                translateY(mouseY.px - 3.px)
            }

            // ✅ Smooth visibility
            opacity(if (visible) 1.0 else 0.0)
            property("transition", "opacity 180ms ease")

            pointerEvents(PointerEvents.None)
            zIndex(2147483647)
        }.toAttrs()
    )
}
